import React, { Fragment, useMemo, useCallback, CSSProperties } from 'react';
import filter from 'lodash/filter';
import includes from 'lodash/includes';

import {
  IsFetched,
  ErrorMessage,
  FetchItemsLoadMoreItems
} from '../../../../../types';
import { TeamNanoID } from '../../../../teams/teamsTypes';
import {
  MaterialNanoID,
  FetchMaterialsFilters,
  ChangeMaterialsFiltersFunc,
  MaterialBrandID,
  FetchMaterialsLimit,
  FetchMaterialsPage,
  UpdateMaterialCacheAction
} from '../../../../materials/materialsTypes';
import {
  ThreeDStockMaterialsListMaterials,
  ThreeDStockMaterialsListMaterial,
  ThreeDStockMaterialsListMaterialsSet
} from './ThreeDStockMaterialsList.types';

import { ThreeDStockMaterialsListItem } from '../ThreeDStockMaterialsListItem';
import { ThreeDStockMaterialsListLightboxTitle } from './components/ThreeDStockMaterialsListLightboxTitle';
import { ThreeDStockMaterialsListLightboxBottomButtons } from './components/ThreeDStockMaterialsListLightboxBottomButtons';

import { AlertMessage } from '../../../../../helpers/AlertMessage';
import { Loading } from '../../../../../helpers/Loading';
import { NoResults } from '../../../../../helpers/NoResults';
import {
  LightboxWrapper,
  useLightboxWrapper,
  LightboxRenderImageTitle,
  LightboxRenderCustomButtons
} from '../../../../../helpers/LightboxWrapper';
import { LoadingGridSkeleton } from '../../../../../helpers/LoadingGridSkeleton';
import { LoadMoreButtonHelper } from '../../../../../helpers/buttons/LoadMoreButtonHelper';

import { Files } from '../../../../../utils/Files';

import { MaterialsPermissions } from '../../../../materials/materialsConstants';
import {
  FetchMaterialsSetsCacheKeys,
  UpdateMaterialsSetCacheAction
} from '../../../../materialsSets/materialsSetsTypes';

type ThreeDStockMaterialsListProps = {
  cellMinSize: number;
  onMaterialEditButtonMouseEnter?: (materialNanoId: MaterialNanoID) => void;
  materials: ThreeDStockMaterialsListMaterials;
  materialsError: ErrorMessage;
  materialsFetched: IsFetched;
  materialsIsPlaceholderData: boolean;
  materialsTotalCount: number;
  updateMaterialCache?: UpdateMaterialCacheAction<ThreeDStockMaterialsListMaterial>;
  materialsFilters: FetchMaterialsFilters;
  changeMaterialsFilters: ChangeMaterialsFiltersFunc;
  hasNextMaterialsPage: boolean;
  materialsPage: FetchMaterialsPage;
  materialsLimit: FetchMaterialsLimit;
  loadMoreMaterials: FetchItemsLoadMoreItems;
  materialsFetchingNextPage: boolean;
  materialsSet: ThreeDStockMaterialsListMaterialsSet | null;
  materialsSetCacheKeys?: FetchMaterialsSetsCacheKeys;
  updateMaterialsSetCache: UpdateMaterialsSetCacheAction<ThreeDStockMaterialsListMaterialsSet>;
  isMyLibrary?: boolean;
  companyNanoId?: TeamNanoID;
};

function ThreeDStockMaterialsList({
  cellMinSize,
  onMaterialEditButtonMouseEnter,
  materials,
  materialsError,
  materialsFetched,
  materialsIsPlaceholderData,
  materialsTotalCount,
  updateMaterialCache,
  materialsFilters,
  changeMaterialsFilters,
  hasNextMaterialsPage,
  materialsPage,
  materialsLimit,
  loadMoreMaterials,
  materialsFetchingNextPage,
  materialsSet,
  updateMaterialsSetCache,
  materialsSetCacheKeys,
  companyNanoId,
  isMyLibrary
}: ThreeDStockMaterialsListProps) {
  const materialsInLightbox = useMemo<ThreeDStockMaterialsListMaterials>(() => {
    return filter<ThreeDStockMaterialsListMaterial>(
      materials,
      (material) => material.image?.file && Files.isImage(material.image.file)
    );
  }, [materials]);

  const handleMaterialBrandButtonClick = useCallback<
    (brandId: MaterialBrandID) => void
  >(
    (brandId) => {
      const currentBrandFilterIds = materialsFilters?.brandId?.in || [];

      if (!includes(currentBrandFilterIds, brandId)) {
        changeMaterialsFilters({
          brandId: { in: [...currentBrandFilterIds, brandId] }
        });
      }
    },
    [changeMaterialsFilters, materialsFilters?.brandId?.in]
  );

  const {
    handleLightboxClose,
    handleLightboxNext,
    handleLightboxOpen,
    handleLightboxOpenOnSlide,
    handleLightboxPrev,
    index,
    imagesCount,
    imageItem,
    lightBoxOpened,
    mainSrc,
    prevSrc,
    nextSrc
  } = useLightboxWrapper({
    items: materialsInLightbox
  });

  const renderImageTitle = useCallback<LightboxRenderImageTitle>(
    ({ index }) => {
      const material = materialsInLightbox[index];
      if (!material) {
        return null;
      }

      return <ThreeDStockMaterialsListLightboxTitle material={material} />;
    },
    [materialsInLightbox]
  );

  const renderLightboxButtons = useCallback<LightboxRenderCustomButtons>(
    ({ index }) => {
      const material = materialsInLightbox[index];
      if (!material) {
        return [];
      }

      return (
        <ThreeDStockMaterialsListLightboxBottomButtons
          material={material}
          materialsSet={materialsSet}
          materialsSetCacheKeys={materialsSetCacheKeys}
          updateMaterialsSetCache={updateMaterialsSetCache}
          updateMaterialCache={updateMaterialCache}
        />
      );
    },
    [
      materialsInLightbox,
      materialsSet,
      materialsSetCacheKeys,
      updateMaterialsSetCache,
      updateMaterialCache
    ]
  );

  const wrapperStyle = useMemo(() => {
    return {
      '--tw-grid-cell-min-size': `${cellMinSize}px`
    };
  }, [cellMinSize]);

  return (
    <Fragment>
      <AlertMessage addClassName="m-4" message={materialsError} />
      <LoadingGridSkeleton
        loaded={materialsIsPlaceholderData || materialsFetched}
      >
        {materialsTotalCount === 0 && <NoResults addErrorClassName="m-4" />}
        {materialsTotalCount > 0 && (
          <div className="flex flex-col space-y-2">
            <Loading
              loaded={!materialsIsPlaceholderData}
              addClass="self-center"
            />
            <div
              className="grid grid-cols-auto-fill auto-rows-min gap-5"
              style={wrapperStyle as CSSProperties}
            >
              {materials.map((material) => (
                <ThreeDStockMaterialsListItem
                  key={material.uuid}
                  material={material}
                  updateMaterialCache={updateMaterialCache}
                  onMaterialEditButtonMouseEnter={
                    onMaterialEditButtonMouseEnter
                  }
                  onMaterialBrandButtonClick={handleMaterialBrandButtonClick}
                  onLightboxOpen={handleLightboxOpenOnSlide}
                  materialsSet={materialsSet}
                  updateMaterialsSetCache={updateMaterialsSetCache}
                  materialsSetCacheKeys={materialsSetCacheKeys}
                  companyNanoId={companyNanoId}
                  isMyLibrary={isMyLibrary}
                />
              ))}
            </div>

            {hasNextMaterialsPage && (
              <LoadMoreButtonHelper
                action={
                  MaterialsPermissions.READ_MATERIALS_LOAD_MORE_BUTTON_ITEMS_COUNT
                }
                dataGa="materials-load-more-button"
                itemsTotalCount={materialsTotalCount}
                page={materialsPage}
                limit={materialsLimit}
                loadMoreItems={loadMoreMaterials}
                itemsFetchingNextPage={materialsFetchingNextPage}
              />
            )}
          </div>
        )}
      </LoadingGridSkeleton>
      <LightboxWrapper
        handleLightboxClose={handleLightboxClose}
        handleLightboxNext={handleLightboxNext}
        handleLightboxOpen={handleLightboxOpen}
        handleLightboxPrev={handleLightboxPrev}
        index={index}
        imagesCount={imagesCount}
        imageItem={imageItem}
        lightBoxOpened={lightBoxOpened}
        mainSrc={mainSrc}
        nextSrc={nextSrc}
        prevSrc={prevSrc}
        renderImageTitle={renderImageTitle}
        renderCustomButtons={renderLightboxButtons}
        withFullScreenButton
      />
    </Fragment>
  );
}

export default ThreeDStockMaterialsList;
